import { Component, inject, OnInit } from '@angular/core';
import { PasswordInputComponent } from '../../components/field-components/password-input/password-input.component';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/authentication/auth.service';
import { matchToOtherControlValidator } from '../../helpers/custom-validators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { ValidIconToggleComponent } from "./valid-icon-toggle/valid-icon-toggle.component";
import { finalize, switchMap, take, tap } from 'rxjs';
import { AppStore } from '../../store/app.store';

export interface ResetPasswordDialogModel {
  key?: string | null;
  orgPolicyDays?: number | null;
  passwordInsecure?: boolean | null;
}

export const RESET_PASSWORD_DIALOG_CONFIG = {
  panelClass: 'dialog-container',
  autoFocus: false,
  width: '40dvw',
  disableClose: true
};

@Component({
    selector: 'app-reset-password-dialog',
    templateUrl: './reset-password-dialog.component.html',
    styleUrls: ['./reset-password-dialog.component.scss'],
    imports: [
        ScrollingModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        PasswordInputComponent,
        ValidIconToggleComponent,
        MatDialogModule,
        MatDialogTitle,
        MatDialogContent
    ]
})
export class ResetPasswordDialogComponent implements OnInit {
  form: FormGroup;

  passwordControlName = 'password';
  confirmPwControlName = 'confirm-password';

  password = new FormControl('', [Validators.required]);
  confirmPassword = new FormControl('');

  orgPolicyDays = -1;

  hasError = false;
  errorMessage = '';

  isDupeErrorName = 'Duplicate Password';
  criteria: { 
    hasUppercase: boolean; 
    hasLowercase: boolean; 
    hasNumber: boolean; 
    hasSpecialChar: boolean; 
    hasValidLength: boolean; 
    hasNoForbiddenWords: boolean;
  } = {
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasValidLength: false,
    hasNoForbiddenWords: false
  };

  forbiddenWords = ['password', 'valuecycle', 'vcpassword', 'welcome', 'letmein', 'changeme', 'admin', 'administrator'];

  public data = inject(MAT_DIALOG_DATA) as ResetPasswordDialogModel;
  private store = inject(AppStore);
  isSubmitting: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ResetPasswordDialogComponent>,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private fb: FormBuilder
  ) {
    this.form = this.fb.group({});
    this.form.addControl(this.passwordControlName, this.password);
    this.form.addControl(this.confirmPwControlName, this.confirmPassword);
  }

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
    const orgParam = this.route.snapshot.queryParamMap.get("orgPolicyDays");
    this.orgPolicyDays = orgParam ? parseInt(orgParam) : -1;
    this.confirmPassword.addValidators([Validators.required, matchToOtherControlValidator(this.passwordControlName)]);
  }

  private getMatchedForbiddenWord(password: string | null | undefined): string | null {
    const normalizedPassword = (password ?? '').toLowerCase();
    if (!normalizedPassword) {
      return null;
    }

    return this.forbiddenWords.find((word) => normalizedPassword.includes(word.toLowerCase())) ?? null;
  }

  checkPasswordCriteria(password: string | null | undefined) {
    password = password?.trim();

    if (!password || password.length === 0) return {
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false,
      hasValidLength: false,
      hasNoForbiddenWords: false
    };

    const containsForbiddenWord = this.getMatchedForbiddenWord(password) !== null;

    return {
      hasUppercase: password.match(/[A-Z]/) !== null,
      hasLowercase: password.match(/[a-z]/) !== null,
      hasNumber: password.match(/[0-9]/) !== null,
      hasSpecialChar: password.match(/[^A-Za-z0-9\s]/) !== null,
      hasValidLength: password.length >= 7 && password.length <= 45,
      hasNoForbiddenWords: !containsForbiddenWord
    };
  }

  onPasswordChange(value: string) {
    this.password.setValue(value);
    this.criteria = this.checkPasswordCriteria(value);
    this.password.markAsTouched();
  }

  onConfirmPasswordChange(value: string) {
    this.confirmPassword.setValue(value);
    this.confirmPassword.markAsTouched();
  }

  getPasswordErrorMessage() {
    if (this.password.hasError('required')) {
      return 'You must enter a value';
    }

    if (this.password?.value && this.password.value.length < 7) {
      return 'Must be at least 7 characters'
    }

  }

  getconfirmPasswordErrorMessage() {
    return this.confirmPassword.hasError('required') ? 'You must enter a value' :
      this.confirmPassword.hasError('notMatching') ? 'Passwords must match' :
        '';
  }

  goToLogin(): void {
    this.router.navigate(["/login"]);
  }

  arePasswordCriteriaMet(): boolean {
    const criteria = this.checkPasswordCriteria(this.password?.value);
    return Object.values(criteria).every(value => value) && 
           this.password?.value === this.confirmPassword?.value;
  }

  onCancel(): void {
    this.dialogRef.close();
    this.goToLogin();
  }

  resetPassword(): void {
    const newPW = this.password?.value;
    if (!newPW) {
      return;
    }
    const key = this.data?.key ?? '';
    this.isSubmitting = true;

    const qp = this.route.snapshot.queryParamMap;
    const username = qp.get('username');
    if (username && username.length > 0) {
      this.store.setUsername(username);
    }

    this.auth.submitNewPassword(key, newPW).pipe(
      switchMap(() => this.auth.login(this.store.username() ?? '', newPW, false)),
      tap(() => this.snackBar.open('Password successfully updated', 'Close', { duration: 3000 })),
      take(1),
      finalize(() => {
        // scrub secrets & re-enable close
        this.password.reset('');
        this.isSubmitting = false;
      })
    ).subscribe({
      next: (ok) => this.dialogRef.close(!!ok),
      error: (error) => {
        this.hasError = true;
        
        const apiMessage = typeof error?.error === 'string' ? error.error : '';

        if (error.status === 400 && apiMessage) {
          if (apiMessage === this.isDupeErrorName) {
            // i.e. this.isDupeErrorName = 'Duplicate Password'
            this.errorMessage = 'Password cannot match your current password';
          } else if (apiMessage === 'Password does not meet complexity requirements.') {
            this.errorMessage = apiMessage;
          } else {
            this.errorMessage = 'Something went wrong updating new password';
          }
        } else {
          this.errorMessage = 'Something went wrong updating new password';
        }
      }
    });
  }

}
